import { RowCrudStatus } from "../../common/types";
import {
  AiSearchPattern,
  AiSearchPatternCreateMutationVariables,
  AiSearchPatternUpdateMutationVariables,
} from "../../../api/graphql";
import { LangStore } from "../../../store";
import { blankToNull } from "../../../common/blankToNull";

export type AiSearchPatternValuesType = {
  id: string;
  displayId: string;
  version_: string;
  __status__: RowCrudStatus;
  name: string;
  description: string;
  pattern: string;
  pathTypes: string[];
  tools: string[];
  effectiveness: number | undefined;
  evaluationCount: number;
  successCount: number;
  sourceEvalIds: string[];
  proposedBy: string;
  active: boolean;
  priority: number;
};

export type AiSearchPatternErrorsType = {
  name?: { message: string; fieldLabel: string };
  description?: { message: string; fieldLabel: string };
  pattern?: { message: string; fieldLabel: string };
  pathTypes?: { message: string; fieldLabel: string };
  tools?: { message: string; fieldLabel: string };
  effectiveness?: { message: string; fieldLabel: string };
  evaluationCount?: { message: string; fieldLabel: string };
  successCount?: { message: string; fieldLabel: string };
  sourceEvalIds?: { message: string; fieldLabel: string };
  proposedBy?: { message: string; fieldLabel: string };
  active?: { message: string; fieldLabel: string };
  priority?: { message: string; fieldLabel: string };
};

export function AiSearchPatternValuesInit(): AiSearchPatternValuesType {
  return {
    id: "",
    displayId: "",
    version_: "",
    __status__: "empty",
    name: "",
    description: "",
    pattern: "",
    pathTypes: [],
    tools: [],
    effectiveness: undefined,
    evaluationCount: 0,
    successCount: 0,
    sourceEvalIds: [],
    proposedBy: "",
    active: false,
    priority: 100,
  };
}

export type AiSearchPatternType = AiSearchPattern | AiSearchPatternValuesType;

export function aiSearchPatternDisplayId(
  source: Partial<AiSearchPattern>,
  _langStore: LangStore
) {
  return source.name ?? _langStore.emptyStringSymbol;
}

export function getAiSearchPatternValues(
  source: AiSearchPatternType,
  _langStore: LangStore,
  _duplicated: boolean = false
): AiSearchPatternValuesType {
  const entityValues = AiSearchPatternValuesInit();
  entityValues.id = source.id;
  entityValues.displayId =
    "__status__" in source
      ? source.displayId
      : aiSearchPatternDisplayId(source, _langStore);
  entityValues.version_ = source.version_;
  entityValues.name = source.name;
  entityValues.description = source.description;
  entityValues.pattern = source.pattern;
  entityValues.pathTypes = source.pathTypes || [];
  entityValues.tools = source.tools || [];
  entityValues.effectiveness =
    (source.effectiveness as unknown) === "" ||
    source.effectiveness === undefined ||
    source.effectiveness === null
      ? undefined
      : source.effectiveness;
  entityValues.evaluationCount = source.evaluationCount || 0;
  entityValues.successCount = source.successCount || 0;
  entityValues.sourceEvalIds = source.sourceEvalIds || [];
  entityValues.proposedBy = source.proposedBy || "";
  entityValues.active = source.active || false;
  entityValues.priority = source.priority || 0;
  if (_duplicated) {
    entityValues.__status__ = "added";
  } else if ("__status__" in source) {
    entityValues.__status__ = source.__status__;
  }
  return entityValues;
}

export function getAiSearchPatternUpdateVariables(
  values: AiSearchPatternValuesType
): AiSearchPatternUpdateMutationVariables {
  return {
    id: values.id,
    version_: values.version_,
    name: values.name,
    description: values.description,
    pattern: values.pattern,
    pathTypes: values.pathTypes || null,
    tools: values.tools || null,
    effectiveness:
      values.effectiveness === undefined ? null : values.effectiveness,
    evaluationCount: values.evaluationCount,
    successCount: values.successCount,
    sourceEvalIds: values.sourceEvalIds || null,
    proposedBy: blankToNull(values.proposedBy),
    active: values.active,
    priority: values.priority,
  };
}

export function getAiSearchPatternCreateVariables(
  values: AiSearchPatternValuesType
): AiSearchPatternCreateMutationVariables {
  return {
    name: values.name,
    description: values.description,
    pattern: values.pattern,
    pathTypes: values.pathTypes || null,
    tools: values.tools || null,
    effectiveness:
      values.effectiveness === undefined ? null : values.effectiveness,
    evaluationCount: values.evaluationCount,
    successCount: values.successCount,
    sourceEvalIds: values.sourceEvalIds || null,
    proposedBy: blankToNull(values.proposedBy),
    active: values.active,
    priority: values.priority,
  };
}
